import { TypeChecker } from '@attain-apps/type-guards'

import type * as common from '../common'
import * as dms from '../dms'
import type * as mosaic from '../mosaic'

import type * as field from './field'

/**
 * Represents a table which can produce content using a {@link Table.Query}
 *
 * @see {@link Table.Column}, {@link Table.Row}
 */
export interface Table {
  // NOTE: intentionally optional so that we are forced to handle it
  columns?: readonly Table.Column[]
  /**
   * Used to get {@link Table.Row rows} for this {@link Table}. We do not
   * include the {@link Table.Row rows} directly on the {@link Table} because
   * the rows can change from pagination, search, sorting, etc. Instead, we
   * include this {@link Table.Query} so that this {@link Table} can simply
   * apply the pagination, search, sorting, etc. to this {@link Table.Query} and
   * then rerun the query.
   */
  query?: Table.Query
  /**
   * Indicates what {@link mosaic.Action} should be executed when the __create__
   * button is clicked on this table
   *
   * `'auto'` => a default workflow is executed to create a row based on the
   * {@link columns}
   */
  onCreate?: mosaic.Action.Ref | 'auto'
  /**
   * Indicates what {@link mosaic.Action} should be executed when the __edit__
   * button is clicked on this {@link Table}
   *
   * #### Special Values
   * - `${selection}` - List of {@link Table.Row.selectionValue} for currently
   *   selected rows
   */
  onBulkEdit?: mosaic.Action.Ref
  /**
   * Indicates what {@link mosaic.Action} should be executed when the __delete__
   * button is clicked on this {@link Table}
   *
   * #### Special Values
   * - `${selection}` - List of {@link Table.Row.selectionValue} for currently
   *   selected rows
   */
  onBulkDelete?: mosaic.Action.Ref
  /**
   * If set to `true`, table pagination will not be included in the query.
   * Instead, whatever is defined in {@link Table.Query} will be applied as is.
   *
   * @default false
   */
  noPagination?: boolean
  /**
   * {@link dms.data.query.Query Query} for displaying the count.
   */
  count?: dms.data.query.Query<number>
  /**
   * {@link dms.data.query.Query Query} for displaying the total row of the table.
   */
  totalRow?: dms.data.query.Query<Table.Row>
}
export namespace Table {
  /**
   * Information about a column of a {@link Table}
   *
   * @see {@link Field.Metadata.Source}
   */
  export interface Column
    extends common.Identified<Column.Id>,
      field.Field.Metadata.Source {
    /**
     * Indicates wether the table sort is enabled for the {@link Column}
     *
     * @default true
     */
    isSortable?: boolean
  }
  export namespace Column {
    /**
     * Identifies a {@link Column}
     */
    export type Id = string
  }

  /**
   * Each property should correspond to a {@link Column.Id} and contain a
   * {@link Field}
   */
  export interface Row {
    fields?: Partial<Record<Column.Id, Field>>
    /**
     * When this {@link Row} is selected, this value will be available to any
     * bulk actions on the {@link Table}
     */
    selectionValue?: unknown
    /**
     * Indicates what {@link mosaic.Action} should be executed when this
     * {@link Row} is clicked
     */
    onClick?: mosaic.Action.Ref
    /**
     * Represents a list {@link mosaic.Action.Ref} shown in the overflow menu of a
     * {@link Row}
     */
    overflowActions?: readonly mosaic.Action.Ref.WithIcon[]
    /**
     * Indicates what {@link mosaic.Action} should be executed when the __edit__
     * button is clicked on this {@link Row}
     *
     * `'auto'` => a default workflow is executed to edit this {@link Row} based
     * on the {@link Table.columns}
     *
     * @deprecated Use {@link Row.overflowActions} instead
     */
    onEdit?: mosaic.Action.Ref | 'auto'
    /**
     * Indicates what {@link mosaic.Action} should be executed when the __delete__
     * button is clicked on this {@link Row}
     *
     * @deprecated Use {@link Row.overflowActions} instead
     */
    onDelete?: mosaic.Action.Ref
  }

  /**
   * Represents a cell in a {@link Row} for a specific {@link Column}
   */
  export type Field = Omit<field.Field, 'meta'> & {
    /** Optional color for the field value resolved by the table query. */
    color?: string
    /**
     * Indicates what {@link mosaic.Action} should be executed when this
     * {@link Field} is clicked
     */
    onClick?: mosaic.Action.Ref
  }

  /**
   * Flat row data for displaying a hierarchical table.
   *
   * The query returns each hierarchy item as one row and includes
   * {@link Tree.Row.parentId parent links}. The UI owns expansion state and
   * renders rows as an indented tree without requiring nested arrays in the
   * query result.
   */
  export interface Tree {
    /**
     * Columns displayed for every row.
     */
    columns?: readonly Column[]
    /**
     * Flat hierarchy rows. Children are linked to parents through
     * {@link Tree.Row.parentId}.
     */
    rows?: readonly Tree.Row[]
    /**
     * Parent record whose direct children should be visible before any row is
     * expanded.
     */
    rootId?: Tree.Row.Id
  }
  export namespace Tree {
    /** One item in the flat hierarchy returned by DMS. */
    export interface Row {
      /** Stable record identifier used for expansion and child lookup. */
      id?: Row.Id
      /** Parent record identifier, if this row belongs under another row. */
      parentId?: Row.Id | null
      /** Cell values keyed by {@link Column.Id}. */
      fields?: Partial<Record<Table.Column.Id, Table.Field>>
    }
    export namespace Row {
      /** DMS record identifier used for hierarchy linkage. */
      export type Id = dms.data.Record.Id
    }
  }

  /**
   * A {@link dms.data.query.Query} that produces a list of {@link Row}
   */
  export type Query = dms.data.query.ForList<Row>
  export namespace Query {
    /**
     * These are things that the user can input to affect what is returned for
     * the {@link Table.Query}
     */
    export interface Modification {
      pagination?: common.Request.For.Pagination.ByPosition
      sort?: common.Request.For.Sort<Column.Id>
      search?: string
      variables?: dms.data.query.Variable.Lookup
    }
    export namespace Modification {
      /**
       * Applies a {@link Modification} to a table {@link Query} to produce a new
       * {@link Query}
       */
      export function apply(
        {
          noPagination,
          ...query
        }: dms.data.query.Query & { noPagination?: boolean } = {},
        modification: Readonly<Modification>,
      ): dms.data.query.Query {
        // Adds variables
        const newQuery = { ...query, variables: modification.variables }
        // apply pagination if noPagination is false and modification includes
        // pagination settings
        if (!noPagination && modification.pagination) {
          // Adds pagination

          // NOTE: this is currently applied all-or-nothing. For example, if
          // query has { quantity: 10, skip: 0 } and modification has {
          // pagination: { skip: 10 } }, the new query will end up with {
          // quantity: undefined, skip: 10 } (i.e. exactly matching the
          // modification instead of merging the modification with the existing
          // query). This is because we assume this is the more predictable and
          // desired behavior
          newQuery.quantity = modification.pagination.quantity
          newQuery.skip = modification.pagination.skip
        }
        if (modification.search) {
          newQuery.variables = {
            ...query.variables,
            // Appending search variable, so we can utilize it in a query e.g.:
            // filter: {
            //   "isSimilar": [
            //     {"@$": "search"},
            //     {"@.": "!search_string"}
            //   ]
            // }
            // TODO: Use reserved variable name for search instead of string literal
            // Ideally should be:
            // [dms.data.query.Variable.Name.Reserved.from(
            //   dms.data.expression.fn.definitions.utils.CommonName.Search,
            // )]: modification.search,
            // Currently using string literal due to backend compatibility issues

            [dms.data.expression.fn.definitions.utils.CommonName.Search]:
              modification.search,
          }
        }
        if (modification.sort) {
          // Adds sort
          newQuery.sort = {
            by: {
              '@>': [
                {
                  '@>': [
                    {
                      '@>': [
                        {
                          '@>': dms.data.query.Variable.Name.Reserved.from(
                            dms.data.expression.fn.definitions.utils.CommonName
                              .Selection,
                          ),
                        },
                        'fields',
                      ],
                    },
                    modification.sort.by,
                  ],
                },
                dms.data.expression.fn.definitions.utils.CommonName.Value,
              ],
            },
            descending: modification.sort.descending,
          }
        }
        return newQuery
      }
    }
  }
}
